// ECharts horizontal bar chart for the loss-reasons distribution —
// FE-SCRM-058 (loss analysis sub-view 1). Mirrors the
// ``CfdChart.tsx`` pattern of registering modules from
// ``echarts/core`` rather than importing the full ECharts bundle,
// keeping vitest's happy-dom canvas init from blowing up (the tile's
// vitest spec stubs this component out entirely).
//
// Bar chart over the pie alternative because reasons are categorical
// and a wide pie is illegible at the dashboard-tile width.

import { BarChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { useEffect, useRef } from "react";

import { LOST_REASON_LABEL } from "./labels";


echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);


export interface LossReasonsChartProps {
  distribution: Record<string, number>;
}


export function LossReasonsChart({ distribution }: LossReasonsChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<ReturnType<typeof echarts.init> | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    instanceRef.current = chart;

    // Sort reasons by count desc so the longest bar is on top — the
    // axis is inverted (highest at top in a horizontal bar chart).
    const entries = Object.entries(distribution).sort(
      ([, a], [, b]) => a - b,
    );
    const categories = entries.map(
      ([k]) => LOST_REASON_LABEL[k] ?? k,
    );
    const values = entries.map(([, v]) => v);

    chart.setOption({
      grid: { top: 8, left: 120, right: 24, bottom: 24 },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "value",
        axisLabel: { fontSize: 10 },
        splitLine: { lineStyle: { color: "#EFEBE0" } },
        minInterval: 1,
      },
      yAxis: {
        type: "category",
        data: categories,
        axisLabel: { fontSize: 10 },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "#D6CDB6" } },
      },
      series: [
        {
          type: "bar",
          data: values,
          itemStyle: { color: "#C24A4A" },
          barWidth: 14,
        },
      ],
    });

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
      instanceRef.current = null;
    };
  }, [distribution]);

  const total = Object.values(distribution).reduce((s, v) => s + v, 0);
  return (
    <div
      data-testid="loss-reasons-chart"
      className="flex flex-col gap-1.5"
    >
      <div
        ref={containerRef}
        data-testid="loss-reasons-chart-canvas"
        className="h-44 w-full"
        role="img"
        aria-label={
          `Distribution of ${total} closed-lost reasons in the window.`
        }
      />
      <ul
        data-testid="loss-reasons-summary"
        className="sr-only"
      >
        {Object.entries(distribution).map(([k, v]) => (
          <li key={k}>
            {LOST_REASON_LABEL[k] ?? k}: {v}
          </li>
        ))}
      </ul>
    </div>
  );
}
